import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Save, MapPin, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const LEAD_STATUSES = [
  'New Lead',
  'Uncontacted',
  'Qualified Lead',
  'Virtual Meet',
  'Awaited Visit',
  'Awaited LOI',
  'Closed LOI',
  'Outlet Opened',
  'Nurture',
  'Dead',
];

const LEAD_SOURCES = [
  'Meta',
  'Website',
  'Referral',
  'Walk-in',
  'Cold Call',
  'Excel Import',
  'Other',
];

const PIN_REGEX = /^[1-9]\d{5}$/;

const EditLeadDialog = ({ open, onClose, lead, executives, onSuccess }) => {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    state: '',
    district: '',
    city: '',
    pinCode: '',
    status: '',
    leadSource: '',
    assignedExecutiveId: '',
    assignedExecutiveName: '',
  });
  const [pinStatus, setPinStatus] = useState('idle');
  const [pinMessage, setPinMessage] = useState('');
  const pinDebounceRef = useRef(null);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        mobile: lead.mobile || '',
        email: lead.email || '',
        state: lead.state || '',
        district: lead.district || '',
        city: lead.city || '',
        pinCode: lead.pinCode || '',
        status: lead.status || '',
        leadSource: lead.leadSource || '',
        assignedExecutiveId: lead.assignedExecutiveId || '',
        assignedExecutiveName: lead.assignedExecutiveName || '',
      });
      if (lead.pinCode && PIN_REGEX.test(lead.pinCode)) {
        setPinStatus('valid');
        setPinMessage('Locked from saved data.');
      } else {
        setPinStatus('idle');
        setPinMessage('');
      }
    }
  }, [lead]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const lookupPin = async (pin) => {
    if (!PIN_REGEX.test(pin)) {
      setPinStatus('invalid_format');
      setPinMessage('PIN must be 6 digits and start with 1–9.');
      setFormData((f) => ({ ...f, state: '', district: '', city: '' }));
      return;
    }
    setPinStatus('checking');
    setPinMessage('Looking up location...');
    try {
      const { data } = await api.get(`/pincode/${pin}`);
      setPinStatus('valid');
      setPinMessage(`✓ ${data.city}, ${data.district}, ${data.state}`);
      setFormData((f) => ({
        ...f,
        pinCode: data.pinCode,
        state: data.state || '',
        district: data.district || '',
        city: data.city || '',
      }));
    } catch (err) {
      if (err?.response?.status === 404) {
        setPinStatus('not_found');
        setPinMessage('PIN not found in India Post master. Please re-check.');
      } else if (err?.response?.status === 422) {
        setPinStatus('invalid_format');
        setPinMessage('PIN must be 6 digits and start with 1–9.');
      } else {
        setPinStatus('error');
        setPinMessage('Lookup failed. Check connection.');
      }
      setFormData((f) => ({ ...f, state: '', district: '', city: '' }));
    }
  };

  const handlePinChange = (raw) => {
    const cleaned = raw.replace(/\D/g, '').slice(0, 6);
    setFormData((f) => ({ ...f, pinCode: cleaned }));
    if (pinDebounceRef.current) clearTimeout(pinDebounceRef.current);
    if (!cleaned) {
      setPinStatus('idle');
      setPinMessage('');
      return;
    }
    if (cleaned.length < 6) {
      setPinStatus('idle');
      setPinMessage('Enter 6 digits to auto-fill State/District/City.');
      return;
    }
    pinDebounceRef.current = setTimeout(() => lookupPin(cleaned), 400);
  };

  const handleExecutiveChange = (executiveId) => {
    const exec = executives?.find((e) => e.id === executiveId);
    setFormData((prev) => ({
      ...prev,
      assignedExecutiveId: executiveId === 'unassigned' ? '' : executiveId,
      assignedExecutiveName: exec?.name || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.mobile) {
      toast.error('Name and Mobile are required');
      return;
    }
    if (formData.pinCode && (pinStatus === 'invalid_format' || pinStatus === 'not_found')) {
      toast.error('Please fix the PIN code or clear it before saving.');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}`, {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email || null,
        state: formData.state || null,
        district: formData.district || null,
        city: formData.city || null,
        pinCode: formData.pinCode || null,
        status: formData.status,
        leadSource: formData.leadSource,
        assignedExecutiveId: formData.assignedExecutiveId || null,
        assignedExecutiveName: formData.assignedExecutiveName || null,
      });
      toast.success('Lead updated — Location synced from PIN');
      onSuccess();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to update lead';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  const locationLocked = pinStatus === 'valid';
  const pinIcon = () => {
    if (pinStatus === 'checking') return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (pinStatus === 'valid') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    if (pinStatus === 'invalid_format' || pinStatus === 'not_found' || pinStatus === 'error')
      return <XCircle className="h-4 w-4 text-destructive" />;
    return <MapPin className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
          <DialogDescription>
            Update lead information. PIN code drives State, District & City — fields are locked when PIN is valid.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input id="edit-name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter name" required data-testid="edit-lead-name" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-mobile">Mobile *</Label>
              <Input id="edit-mobile" value={formData.mobile} onChange={(e) => handleChange('mobile', e.target.value)}
                placeholder="Enter mobile number" required data-testid="edit-lead-mobile" />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input id="edit-email" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Enter email" data-testid="edit-lead-email" />
            </div>

            {/* PIN first */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="edit-pincode" className="flex items-center gap-2">
                Pin Code {pinIcon()}
              </Label>
              <Input
                id="edit-pincode"
                inputMode="numeric"
                maxLength={6}
                value={formData.pinCode}
                onChange={(e) => handlePinChange(e.target.value)}
                onBlur={() => formData.pinCode && lookupPin(formData.pinCode)}
                placeholder="6-digit India PIN (e.g. 400001)"
                data-testid="edit-lead-pincode"
                className={
                  pinStatus === 'invalid_format' || pinStatus === 'not_found'
                    ? 'border-destructive'
                    : pinStatus === 'valid'
                    ? 'border-emerald-500'
                    : ''
                }
              />
              {pinMessage && (
                <p
                  className={`text-xs ${
                    pinStatus === 'valid'
                      ? 'text-emerald-600'
                      : pinStatus === 'invalid_format' || pinStatus === 'not_found' || pinStatus === 'error'
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  }`}
                  data-testid="edit-lead-pin-feedback"
                >
                  {pinMessage}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-state">State {locationLocked && <span className="text-xs text-muted-foreground">(auto)</span>}</Label>
              <Input id="edit-state" value={formData.state} onChange={(e) => handleChange('state', e.target.value)}
                disabled={locationLocked} placeholder="Auto from PIN" data-testid="edit-lead-state"
                className={locationLocked ? 'bg-muted cursor-not-allowed' : ''} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-district">District {locationLocked && <span className="text-xs text-muted-foreground">(auto)</span>}</Label>
              <Input id="edit-district" value={formData.district} onChange={(e) => handleChange('district', e.target.value)}
                disabled={locationLocked} placeholder="Auto from PIN" data-testid="edit-lead-district"
                className={locationLocked ? 'bg-muted cursor-not-allowed' : ''} />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="edit-city">City {locationLocked && <span className="text-xs text-muted-foreground">(auto)</span>}</Label>
              <Input id="edit-city" value={formData.city} onChange={(e) => handleChange('city', e.target.value)}
                disabled={locationLocked} placeholder="Auto from PIN" data-testid="edit-lead-city"
                className={locationLocked ? 'bg-muted cursor-not-allowed' : ''} />
            </div>

            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger data-testid="edit-lead-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Lead Source</Label>
              <Select value={formData.leadSource} onValueChange={(v) => handleChange('leadSource', v)}>
                <SelectTrigger data-testid="edit-lead-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Assigned To</Label>
              <Select value={formData.assignedExecutiveId || 'unassigned'} onValueChange={handleExecutiveChange}>
                <SelectTrigger data-testid="edit-lead-executive"><SelectValue placeholder="Select executive" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {executives?.map((exec) => (
                    <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-lead-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditLeadDialog;
